// ttsPlayback.js — turn a /api/tts Response into a playing <audio> element & Jarvis-grade sentence-primed TTS queue.
//
// /api/tts returns one of three shapes:
//   1. ElevenLabs  — audio/mpeg, X-TTS-Streaming: mp3 → progressive playback via
//      MediaSource where supported (Chrome/Edge/desktop Safari), else full blob.
//   2. Piper       — application/octet-stream, X-TTS-Streaming: pcm-raw. Body is a
//      JSON header line + raw s16le PCM. Wrapped in a WAV header here so it plays.
//   3. Everything else (cache hits, MiniMax, OpenAI) — complete audio/* file → blob.
//
// Also exports REA-verified adewaskar/jarvis TTS, Echo, Filler & SFX primitives:
//   - createSpeaker({ fetchTts, agent, voiceName }): streaming sentence-boundary TTS queue
//     with N+1 sentence lookahead priming (prime(item); prime(queue[0])), abbreviation guard,
//     MAX_UNSPOKEN=220 fallback, native speechSynthesis un-wedging (speechSynthesis.resume()
//     + 700ms/1500ms start watchdog + 5000ms keepalive), and audio.onpause barge-in settlement.
//   - speakingNow(), speakingSince(), setSpeaking(text), shape(text), ttsDiag (window.__tts).
//   - isEcho(heard, spoken), OVERRIDE, STOP, SELF_GUARD_MS (shared acoustic echo filter).
//   - forTool(name) (non-repeating tool-specific spoken latency fillers from fillers.ts).
//   - playSfx(kind), duckSfx(active) (zero-asset WebAudio procedural HUD cues + auto-ducking from sfx.ts).

export function pcmToWav(pcm, sampleRate, channels, bitDepth) {
  const header = new ArrayBuffer(44);
  const v = new DataView(header);
  const w = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  const blockAlign = channels * (bitDepth / 8);
  w(0, "RIFF"); v.setUint32(4, 36 + pcm.byteLength, true); w(8, "WAVE");
  w(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, channels, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * blockAlign, true);
  v.setUint16(32, blockAlign, true); v.setUint16(34, bitDepth, true);
  w(36, "data"); v.setUint32(40, pcm.byteLength, true);
  return new Blob([header, pcm], { type: "audio/wav" });
}

export async function piperResponseToWav(resp) {
  const bytes = new Uint8Array(await resp.arrayBuffer());
  const nl = bytes.indexOf(10); // header line ends at first "\n"
  if (nl < 0) throw new Error("piper: missing header");
  let fmt = { sampleRate: 22050, channels: 1, bitDepth: 16 };
  try { fmt = { ...fmt, ...JSON.parse(new TextDecoder().decode(bytes.subarray(0, nl))) }; } catch (_) {}
  const pcm = bytes.subarray(nl + 1);
  if (pcm.byteLength < 100) throw new Error("piper: empty audio");
  return pcmToWav(pcm, fmt.sampleRate, fmt.channels, fmt.bitDepth);
}

function canStreamMp3() {
  try { return !!(typeof window !== "undefined" && window.MediaSource && MediaSource.isTypeSupported("audio/mpeg")); } catch { return false; }
}

// Feed a streaming MP3 body into a MediaSource so playback starts on the first chunk.
function streamMp3(resp, audio) {
  const ms = new MediaSource();
  const url = URL.createObjectURL(ms);
  audio.src = url;
  ms.addEventListener("sourceopen", async () => {
    let sb;
    try { sb = ms.addSourceBuffer("audio/mpeg"); } catch (e) { audio.dispatchEvent(new Event("error")); return; }
    const waitIdle = () => sb.updating ? new Promise(r => sb.addEventListener("updateend", r, { once: true })) : Promise.resolve();
    const reader = resp.body.getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        await waitIdle();
        sb.appendBuffer(value);
      }
      await waitIdle();
      if (ms.readyState === "open") ms.endOfStream();
    } catch (_) {
      try { if (ms.readyState === "open") ms.endOfStream(); } catch (__) {}
    }
  }, { once: true });
  return url;
}

// Returns { audio, url } ready to play, or throws if the response holds no playable audio.
export async function audioFromTtsResponse(resp) {
  const ct = resp.headers.get("content-type") || "";
  const streaming = resp.headers.get("x-tts-streaming") || "";
  const audio = typeof Audio !== "undefined" ? new Audio() : { src: "" };
  if (streaming === "pcm-raw") {
    const blob = await piperResponseToWav(resp);
    const url = typeof URL !== "undefined" && URL.createObjectURL ? URL.createObjectURL(blob) : "blob:piper";
    audio.src = url;
    return { audio, url };
  }
  if (!ct.startsWith("audio/")) throw new Error("tts non-audio response");
  if (streaming === "mp3" && resp.body && canStreamMp3()) {
    return { audio, url: streamMp3(resp, audio) };
  }
  const blob = await resp.blob();
  const url = typeof URL !== "undefined" && URL.createObjectURL ? URL.createObjectURL(blob) : "blob:audio";
  audio.src = url;
  return { audio, url };
}

// ---------------------------------------------------------------------------
// Jarvis AI (adewaskar/jarvis) TTS Echo State, Sentence Boundary & N+1 Priming
// ---------------------------------------------------------------------------

let speaking = "";
let recent = "";
let recentUntil = 0;
let speakingAt = 0;

/** Recognition lags the speakers by a few hundred milliseconds, so a sentence
 *  keeps arriving at the microphone well after it has finished playing. */
export const ECHO_TAIL_MS = 1800;

/** Sentence end, allowing a closing quote or bracket — curly ones included. */
export const SENTENCE_END = /([.!?]["'')\]”’]?\s)|(\n\n)/;

/** Full stops that are not sentence ends (prevents splitting "Mr. Stark"). */
export const ABBREVIATION =
  /(?:^|\s)(mr|mrs|ms|dr|prof|sr|jr|st|vs|etc|e\.g|i\.e|approx|inc|ltd|co|no|vol|fig|dept|est|min|max|hr|hrs|a\.m|p\.m|u\.s|u\.k|no)\.$/i;

/** Past this many characters without punctuation, cut at the last word boundary. */
export const MAX_UNSPOKEN = 220;

export const ttsDiag = {
  engine: "elevenlabs",
  spoken: 0,
  started: 0,
  failures: 0,
  lastError: "",
  nativeBroken: false,
  rescued: 0,
  voice: "",
  lastText: "",
};

if (typeof window !== "undefined") {
  window.__tts = ttsDiag;
}

/** When the current sentence started, or 0 if nothing is being spoken. */
export function speakingSince() {
  return speaking ? speakingAt : 0;
}

export function setSpeaking(text) {
  if (text) {
    speaking = text;
    speakingAt = Date.now();
    return;
  }
  if (speaking) {
    recent = speaking;
    recentUntil = Date.now() + ECHO_TAIL_MS;
  }
  speaking = "";
}

/** What the microphone is likely to be hearing from the speakers right now (including ECHO_TAIL_MS tail). */
export function speakingNow() {
  const tail = Date.now() < recentUntil ? recent : "";
  return `${speaking} ${tail}`.trim();
}

export const SELF_GUARD_MS = 350;

export const OVERRIDE =
  /\b(stop|wait|kara|lala|jarvis|cancel|enough|quiet|hold on|shut up|never ?mind|forget it|no)\b/i;

export const STOP = new Set(
  (
    "a an the and or but so of to in on at by for with from is are was were be " +
    "it its this that these those i you he she we they me him her them my your " +
    "our their what which who how why when where do does did can could would " +
    "should will shall not no yes if then than as about into over under out up " +
    "down one two three first second third now here there just very really got " +
    "get have has had say said tell me okay ok well right"
  ).split(" "),
);

const normEcho = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9' ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Bag-of-content-words acoustic echo filter (adewaskar/jarvis voice.ts).
 * Compares heard text against speakingNow() (which includes ECHO_TAIL_MS=1800ms tail).
 */
export function isEcho(heard, spoken = speakingNow()) {
  if (!spoken) return false;
  if (OVERRIDE.test(String(heard || ""))) return false;

  const all = normEcho(heard).split(" ").filter(Boolean);
  if (!all.length) return true;

  const mine = new Set(normEcho(spoken).split(" "));
  const content = all.filter((w) => !STOP.has(w));

  if (content.length < 2) {
    if (all.length < 2) return false;
    return all.every((w) => mine.has(w));
  }

  let hits = 0;
  for (const w of content) if (mine.has(w)) hits++;
  return hits / content.length >= 0.6;
}

// ---------------------------------------------------------------------------
// Jarvis AI (adewaskar/jarvis) Tool-Specific Non-Repeating Fillers (fillers.ts)
// ---------------------------------------------------------------------------
const BY_TOOL = {
  run_backtest: ["Running the backtest now.", "Pulling the backtest numbers.", "Simulating that strategy now."],
  live_replay_backtest: ["Running the live parity replay now.", "Checking the scanner parity replay."],
  scan_symbols: ["Scanning the market now.", "Checking all active symbols.", "Sweeping the live feeds."],
  activate_strategy: ["Switching the active strategy now.", "Updating the strategy engine."],
  check_balance: ["Checking your Deriv account balance.", "Pulling your live balance now."],
  search_second_brain: ["Checking the Second Brain vault.", "Searching the knowledge graph."],
  web_search: ["Checking the live feeds.", "Looking that up now.", "Pulling the latest data."],
  execute_trade: ["Routing the trade order now.", "Preparing the execution ticket."],
};

const GENERAL_FILLERS = [
  "One moment.",
  "Checking now.",
  "Right away.",
  "Let me check.",
  "On it.",
];

let lastFillerPicked = "";

function pickFillerFrom(pool) {
  const choices = pool.length > 1 ? pool.filter((p) => p !== lastFillerPicked) : pool;
  const choice = choices[Math.floor(Math.random() * choices.length)] || pool[0] || "One moment.";
  lastFillerPicked = choice;
  return choice;
}

export function forTool(name = "") {
  const key = String(name || "").toLowerCase().trim();
  return pickFillerFrom(BY_TOOL[key] ?? GENERAL_FILLERS);
}

// ---------------------------------------------------------------------------
// Jarvis AI (adewaskar/jarvis) Zero-Asset WebAudio Procedural HUD Cues (sfx.ts)
// ---------------------------------------------------------------------------
let sfxCtx = null;
let sfxMaster = null;
let sfxMuted = false;

function ensureSfxBus() {
  if (sfxMuted) return null;
  try {
    const Ctx = typeof AudioContext !== "undefined" ? AudioContext : (typeof webkitAudioContext !== "undefined" ? webkitAudioContext : null);
    if (!Ctx) return null;
    if (!sfxCtx) {
      sfxCtx = new Ctx();
      sfxMaster = sfxCtx.createGain();
      sfxMaster.gain.value = 0.25;
      sfxMaster.connect(sfxCtx.destination);
    }
    if (sfxCtx.state === "suspended") void sfxCtx.resume?.();
    return { c: sfxCtx, out: sfxMaster };
  } catch {
    return null;
  }
}

function sfxTone(c, out, freq, start, dur, peak = 0.08, type = "sine", bendTo = 0) {
  try {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, start);
    if (bendTo) osc.frequency.exponentialRampToValueAtTime(bendTo, start + dur);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + Math.min(0.015, dur * 0.25));
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain);
    gain.connect(out);
    osc.start(start);
    osc.stop(start + dur + 0.01);
  } catch {}
}

export function playSfx(kind) {
  const bus = ensureSfxBus();
  if (!bus || !bus.c || !bus.out) return;
  const { c, out } = bus;
  const now = c.currentTime || 0;
  switch (kind) {
    case "wake":
      sfxTone(c, out, 520, now, 0.09, 0.07, "sine", 540);
      sfxTone(c, out, 780, now + 0.065, 0.16, 0.08, "sine", 820);
      break;
    case "listen":
      sfxTone(c, out, 880, now, 0.055, 0.045, "triangle");
      break;
    case "think":
      sfxTone(c, out, 240, now, 0.14, 0.04, "sine", 320);
      break;
    case "done":
      sfxTone(c, out, 660, now, 0.08, 0.05, "sine");
      sfxTone(c, out, 520, now + 0.06, 0.12, 0.045, "sine");
      break;
    case "error":
      sfxTone(c, out, 220, now, 0.07, 0.06, "triangle");
      sfxTone(c, out, 180, now + 0.08, 0.09, 0.06, "triangle");
      break;
    default:
      break;
  }
}

export function duckSfx(active) {
  if (!sfxCtx || !sfxMaster) return;
  try {
    const now = sfxCtx.currentTime || 0;
    sfxMaster.gain.cancelScheduledValues?.(now);
    sfxMaster.gain.linearRampToValueAtTime?.(active ? 0.08 : 0.25, now + 0.08);
  } catch {}
}

export function setSfxMuted(m) {
  sfxMuted = !!m;
}

/** Strip markdown, URLs, and nudge vocative cadence before synthesis. */
export function shape(text) {
  return String(text || "")
    .replace(/^\s*Sources?:.*$/gim, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/[^\s]*[^\s.,;:!?)\]]/g, "")
    .replace(/[*_`#>]+/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/([^,\s])\s+(sir)(\s*[.,!?;:]|\s*$)/gi, "$1, $2$3")
    .replace(/\s+/g, " ")
    .trim();
}

let outCtx = null;
function outputContext() {
  try {
    if (typeof AudioContext === "undefined" && typeof webkitAudioContext === "undefined") return null;
    const Ctx = typeof AudioContext !== "undefined" ? AudioContext : webkitAudioContext;
    if (!outCtx) outCtx = new Ctx();
    if (outCtx.state === "suspended") void outCtx.resume();
    return outCtx;
  } catch {
    return null;
  }
}

async function defaultFetchCloudTts(text, agent = "kara") {
  if (typeof fetch === "undefined") return null;
  try {
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("kara_auth_token") || "" : "";
    const resp = await fetch("/api/tts", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ text, agent, mode: "chat", quality: "fast" }),
    });
    if (!resp.ok) return null;
    return await audioFromTtsResponse(resp);
  } catch {
    return null;
  }
}

/**
 * Create a Jarvis-grade sentence-primed speaker queue.
 * Priming runs N+1 (prime(item) and prime(queue[0])) so sentence N+1 is synthesized
 * while sentence N plays, eliminating inter-sentence gaps without flooding /api/tts.
 */
export function createSpeaker(options = {}) {
  const {
    fetchTts = defaultFetchCloudTts,
    agent = "kara",
    onStartSpeaking = null,
    onStopSpeaking = null,
  } = options;

  const queue = [];
  let buffer = "";
  let cancelled = false;
  let outLevel = 0;
  let pumping = false;

  let currentAudio = null;
  let nativeInFlight = false;
  let drained = [];

  const settleDrained = () => {
    const waiting = drained;
    drained = [];
    for (const r of waiting) r();
  };

  function synthesise(text) {
    ttsDiag.engine = "elevenlabs";
    return Promise.resolve()
      .then(() => fetchTts(text, agent))
      .catch(() => null);
  }

  /** Start generating an item's audio if it hasn't begun (N+1 lookahead priming). */
  const prime = (item) => {
    if (item && item.audio === undefined) {
      item.audio = synthesise(item.text);
    }
  };

  const enqueue = (sentence, priority = false) => {
    if (cancelled) return;
    const text = shape(sentence);
    if (!text) return;

    const item = { text, audio: undefined };
    if (priority) {
      queue.unshift(item);
    } else {
      queue.push(item);
    }
    // If pump is already playing sentence N, immediately prime queue[0] (sentence N+1)
    if (pumping) {
      prime(queue[0]);
    } else {
      void pump();
    }
  };

  async function pump() {
    if (pumping) return;
    pumping = true;
    try {
      for (;;) {
        if (cancelled) break;
        const item = queue.shift();
        if (!item) break;

        // Prime current item and N+1 lookahead sentence in queue[0]
        prime(item);
        prime(queue[0]);

        await speakOne(item);
      }
    } finally {
      pumping = false;
      if (cancelled || !queue.length) {
        onStopSpeaking?.();
        settleDrained();
      }
    }
  }

  async function speakOne(item) {
    if (cancelled) return;
    setSpeaking(item.text);
    duckSfx(true);
    onStartSpeaking?.(item.text);
    try {
      const resolved = item.audio ? await item.audio : null;
      if (cancelled) return;
      if (resolved && (resolved.audio || resolved.url || typeof resolved === "string")) {
        await playResolvedAudio(resolved, item.text);
        return;
      }

      const spoke = await speakNative(item.text);
      if (spoke || cancelled) return;

      if (!ttsDiag.nativeBroken) {
        ttsDiag.nativeBroken = true;
        ttsDiag.engine = "elevenlabs";
      }
      const rescue = await synthesise(item.text);
      if (rescue && !cancelled) {
        ttsDiag.rescued++;
        await playResolvedAudio(rescue, item.text);
      }
    } finally {
      if (speaking === item.text) setSpeaking("");
      if (!queue.length) duckSfx(false);
    }
  }

  const speakNative = (text) =>
    new Promise((resolve) => {
      if (typeof window === "undefined" || !window.speechSynthesis || typeof SpeechSynthesisUtterance === "undefined") {
        resolve(false);
        return;
      }
      try { window.speechSynthesis.resume(); } catch (_) {}

      const u = new SpeechSynthesisUtterance(text);
      try {
        const voices = window.speechSynthesis.getVoices?.() || [];
        const preferred =
          voices.find((v) => /en[-_]?(GB|US|AU|IN)/i.test(v.lang) && /Google|Microsoft|Samantha|Aria|Daniel/i.test(v.name)) ||
          voices.find((v) => /^en/i.test(v.lang));
        if (preferred) u.voice = preferred;
        u.lang = preferred?.lang || "en-US";
      } catch (_) {}
      u.rate = 1.02;
      u.pitch = 1.0;
      u.volume = 0.95;

      let raf = 0;
      let t = 0;
      const tick = () => {
        t += 0.08;
        outLevel = 0.35 + Math.abs(Math.sin(t * 2.1)) * 0.3 + Math.abs(Math.sin(t * 5.7)) * 0.2;
        if (typeof requestAnimationFrame === "function") raf = requestAnimationFrame(tick);
      };
      if (typeof requestAnimationFrame === "function") tick();

      let done = false;
      let started = false;
      let watchdog = null;
      let keepalive = null;

      const finish = () => {
        if (done) return;
        done = true;
        nativeInFlight = false;
        if (watchdog) clearTimeout(watchdog);
        if (keepalive) clearInterval(keepalive);
        if (raf && typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf);
        outLevel = 0.12;
        resolve(started);
      };

      u.onstart = () => {
        started = true;
        ttsDiag.started++;
        ttsDiag.lastError = "";
        if (watchdog) clearTimeout(watchdog);
        keepalive = setInterval(() => {
          if (done) return;
          try {
            window.speechSynthesis.pause();
            window.speechSynthesis.resume();
          } catch (_) {}
        }, 5000);
      };
      u.onend = finish;
      u.onerror = (e) => {
        const code = String(e?.error ?? "unknown");
        ttsDiag.lastError = code;
        if (code !== "interrupted" && code !== "canceled") {
          ttsDiag.failures++;
        }
        finish();
      };

      watchdog = setTimeout(() => {
        if (done || started) return;
        try {
          window.speechSynthesis.cancel();
          window.speechSynthesis.resume();
          window.speechSynthesis.speak(u);
        } catch {
          finish();
          return;
        }
        watchdog = setTimeout(() => {
          if (done || started) return;
          ttsDiag.failures++;
          ttsDiag.lastError = ttsDiag.lastError || "no-start";
          finish();
        }, 1500);
      }, 700);

      ttsDiag.spoken++;
      ttsDiag.lastText = text.slice(0, 60);
      ttsDiag.voice = u.voice?.name ?? "default";
      nativeInFlight = true;
      try {
        window.speechSynthesis.speak(u);
      } catch {
        finish();
      }
    });

  const playResolvedAudio = (resolved, text) =>
    new Promise((resolve) => {
      const url = typeof resolved === "string" ? resolved : resolved.url;
      const audio = (typeof resolved === "object" && resolved?.audio) ? resolved.audio : new Audio(url);
      currentAudio = audio;
      ttsDiag.spoken++;
      ttsDiag.lastText = text.slice(0, 60);
      ttsDiag.voice = "ElevenLabs";

      let read = null;
      const ctx = outputContext();
      if (ctx && typeof audio === "object") {
        try {
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          ctx.createMediaElementSource(audio).connect(analyser);
          analyser.connect(ctx.destination);
          const bins = new Uint8Array(analyser.frequencyBinCount);
          read = () => {
            analyser.getByteFrequencyData(bins);
            let sum = 0;
            for (let i = 2; i < bins.length; i++) sum += bins[i];
            return Math.min(1, (sum / (bins.length - 2) / 255) * 3.5);
          };
        } catch {
          /* analyser is optional */
        }
      }

      let raf = 0;
      const tick = () => {
        outLevel = read ? read() : 0.4;
        if (typeof requestAnimationFrame === "function") raf = requestAnimationFrame(tick);
      };
      if (typeof requestAnimationFrame === "function") tick();

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        if (raf && typeof cancelAnimationFrame === "function") cancelAnimationFrame(raf);
        outLevel = 0.12;
        if (url && typeof URL !== "undefined" && URL.revokeObjectURL) {
          try { URL.revokeObjectURL(url); } catch (_) {}
        }
        if (currentAudio === audio) currentAudio = null;
        resolve();
      };

      audio.onplaying = () => {
        ttsDiag.started++;
        ttsDiag.lastError = "";
      };
      audio.onended = finish;
      audio.onerror = () => {
        ttsDiag.failures++;
        ttsDiag.lastError = "audio-element";
        finish();
      };
      // Crucial for barge-in: cancel() pauses the element, and a paused element never fires `ended`.
      audio.onpause = finish;
      Promise.resolve()
        .then(() => audio.play())
        .catch((err) => {
          ttsDiag.failures++;
          ttsDiag.lastError = String(err?.name ?? "play-rejected");
          finish();
        });
    });

  return {
    say(text) {
      enqueue(text, true);
    },
    push(delta) {
      if (cancelled) return;
      buffer += delta;

      for (;;) {
        const m = SENTENCE_END.exec(buffer);
        if (!m) break;
        const cut = m.index + m[0].length;
        const candidate = buffer.slice(0, cut);
        if (ABBREVIATION.test(candidate.trimEnd())) {
          const rest = buffer.slice(cut);
          if (!SENTENCE_END.test(rest)) break;
          const next = SENTENCE_END.exec(rest);
          const wider = cut + next.index + next[0].length;
          enqueue(buffer.slice(0, wider));
          buffer = buffer.slice(wider);
          continue;
        }
        enqueue(candidate);
        buffer = buffer.slice(cut);
      }

      if (buffer.length > MAX_UNSPOKEN) {
        const cut = buffer.lastIndexOf(" ", MAX_UNSPOKEN);
        if (cut > 40) {
          enqueue(buffer.slice(0, cut));
          buffer = buffer.slice(cut);
        }
      }
    },
    async end() {
      if (buffer.trim()) {
        enqueue(buffer);
        buffer = "";
      }
      if (cancelled) return;
      if (!pumping && !queue.length) return;
      await new Promise((resolve) => drained.push(resolve));
    },
    cancel() {
      if (cancelled) return;
      cancelled = true;
      buffer = "";
      queue.length = 0;
      // Preserve ECHO_TAIL_MS on what was being spoken so acoustic echo filter still catches residual audio.
      setSpeaking("");

      if (nativeInFlight && typeof window !== "undefined" && window.speechSynthesis) {
        nativeInFlight = false;
        try {
          window.speechSynthesis.cancel();
          window.speechSynthesis.resume();
        } catch (_) {}
      }
      if (currentAudio) {
        try { currentAudio.pause(); } catch (_) {}
        currentAudio = null;
      }
      outLevel = 0;
      duckSfx(false);
      onStopSpeaking?.();
      settleDrained();
    },
    level: () => outLevel,
    queueSize: () => queue.length,
  };
}
